'use client';

/**
 * HighlightedCode — syntax-highlighted source for `code` artifacts.
 *
 * Reuses the #2138 highlight infrastructure: lowlight with the `common`
 * grammar set (the same set rehype-highlight uses for fenced markdown blocks),
 * the shared hast → React walker (no dangerouslySetInnerHTML on model
 * output), and the `code-highlight.css` token theme scoped under
 * `.meridian-hljs`. Unknown/unregistered languages render as plain monospace
 * text — never a crash.
 */

import React, { useMemo } from 'react';
import { createLowlight, common } from 'lowlight';
import { renderHastNodes } from '../ui/highlight-hast';
import '../code-highlight.css';

const lowlight = createLowlight(common);

/** Aliases the model plausibly emits that map onto registered grammars. */
const LANGUAGE_ALIASES: Record<string, string> = {
  ts: 'typescript',
  tsx: 'typescript',
  js: 'javascript',
  jsx: 'javascript',
  py: 'python',
  sh: 'bash',
  shell: 'bash',
  yml: 'yaml',
  'c++': 'cpp',
};

function resolveLanguage(language: string | undefined): string | null {
  if (!language) return null;
  const normalized = language.trim().toLowerCase();
  const resolved = LANGUAGE_ALIASES[normalized] ?? normalized;
  return lowlight.registered(resolved) ? resolved : null;
}

export interface HighlightedCodeProps {
  code: string;
  /** Language hint (e.g. "typescript"); unknown values fall back to plain text. */
  language?: string;
}

export function HighlightedCode({ code, language }: HighlightedCodeProps) {
  const highlighted = useMemo(() => {
    const resolved = resolveLanguage(language);
    if (!resolved) return null;
    try {
      const tree = lowlight.highlight(resolved, code);
      return renderHastNodes(tree.children, 'hljs-code');
    } catch {
      // Grammar hiccup on unusual input — plain text is always safe.
      return null;
    }
  }, [code, language]);

  return (
    <code className={`hljs font-mono${language ? ` language-${language}` : ''}`}>
      {highlighted ?? code}
    </code>
  );
}
